/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material"
import { collection, query, where, onSnapshot, addDoc, doc, deleteDoc } from "firebase/firestore"
import { db } from "../../firebase"
import { tokens } from "../../theme"
import { useNavigate } from "react-router-dom"
import AddIcon from "@mui/icons-material/Add"
import dayjs from "dayjs"
import { toast } from 'react-toastify'
import { IBusinessHoliday, isDateInHolidays } from "../../utils/businessHolidays"

const MONTHS = [
  { value: "01", label: "1月" },
  { value: "02", label: "2月" },
  { value: "03", label: "3月" },
  { value: "04", label: "4月" },
  { value: "05", label: "5月" },
  { value: "06", label: "6月" },
  { value: "07", label: "7月" },
  { value: "08", label: "8月" },
  { value: "09", label: "9月" },
  { value: "10", label: "10月" },
  { value: "11", label: "11月" },
  { value: "12", label: "12月" },
]

interface DailyHygieneData {
  id?: string
  year: string
  month: string
  day: string
  date: string
  isHoliday?: boolean
  manager?: string
  facilityChecks?: Record<string, string>
  handwashChecks?: Record<string, string>
  toiletChecks?: Record<string, string>
  equipmentChecks?: Record<string, string>
  pestChecks?: Record<string, string>
  waterChecks?: Record<string, string>
  wasteChecks?: Record<string, string>
  verifier?: string
  remarks?: string
  createdAt?: string
  updatedAt?: string
}

const DailyHygieneManagement = () => {
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const navigate = useNavigate()
  const [records, setRecords] = useState<Record<string, DailyHygieneData>>({})
  const [holidays, setHolidays] = useState<IBusinessHoliday[]>([])
  const [selectedYear, setSelectedYear] = useState<string>(dayjs().format("YYYY"))
  const [selectedMonth, setSelectedMonth] = useState<string>(dayjs().format("MM"))
  const [daysInMonth, setDaysInMonth] = useState<number>(31)

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "businessHolidays"), (snapshot) => {
      setHolidays(snapshot.docs.map(d => ({ id: d.id, ...d.data() } as IBusinessHoliday)))
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    const days = dayjs(`${selectedYear}-${selectedMonth}-01`).daysInMonth()
    setDaysInMonth(days)

    const hygieneManagementsRef = collection(db, "dailyHygieneManagements")
    const q = query(
      hygieneManagementsRef,
      where("year", "==", selectedYear),
      where("month", "==", selectedMonth)
    )

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const recordsMap: Record<string, DailyHygieneData> = {}
      snapshot.docs.forEach(doc => {
        const data = doc.data() as DailyHygieneData
        recordsMap[data.day] = {
          id: doc.id,
          ...data
        }
      })
      setRecords(recordsMap)
    })

    return unsubscribe
  }, [selectedYear, selectedMonth])

  const getMonthLabel = (monthValue: string) => {
    const monthObj = MONTHS.find(m => m.value === monthValue)
    return monthObj ? monthObj.label : monthValue
  }

  const handleNew = (day: number) => {
    const dateStr = `${selectedYear}-${selectedMonth}-${day.toString().padStart(2, '0')}`
    navigate(`/admin/daily-hygiene-management/new?date=${dateStr}`)
  }

  const handleEdit = (day: number) => {
    const dayStr = day.toString()
    const record = records[dayStr]
    if (record?.id) {
      navigate(`/admin/daily-hygiene-management/${record.id}`)
    }
  }

  const hasRecord = (day: number) => {
    return !!records[day.toString()]
  }

  const isLocalHoliday = (day: number) => {
    const record = records[day.toString()]
    return record?.isHoliday === true
  }

  const isMasterHoliday = (day: number) => {
    const dateStr = `${selectedYear}-${selectedMonth}-${day.toString().padStart(2, '0')}`
    return isDateInHolidays(dateStr, holidays)
  }

  const handleSetHoliday = async (day: number) => {
    const dayStr = day.toString()
    const record = records[dayStr]

    if (record?.id && record.isHoliday) {
      // 既に休日として登録されている場合は削除
      try {
        await deleteDoc(doc(db, "dailyHygieneManagements", record.id))
        toast.success("休日設定を解除しました")
      } catch (e) {
        toast.error("休日設定の解除に失敗しました")
      }
    } else if (record?.id && !record.isHoliday) {
      // 通常データが既にある場合は休日に変更できない
      toast.error("既にデータが入力されています。休日に設定する場合は先にデータを削除してください。")
    } else {
      // 休日として新規登録
      try {
        const dateStr = `${selectedYear}-${selectedMonth}-${day.toString().padStart(2, '0')}`
        await addDoc(collection(db, "dailyHygieneManagements"), {
          year: selectedYear,
          month: selectedMonth,
          day: dayStr,
          date: dateStr,
          isHoliday: true,
          createdAt: dayjs().format("YYYY-MM-DD HH:mm:ss"),
          updatedAt: dayjs().format("YYYY-MM-DD HH:mm:ss")
        })
        toast.success("休日として登録しました")
      } catch (e) {
        toast.error("休日登録に失敗しました")
      }
    }
  }

  return (
    <Box m={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          衛生管理点検票（日次）
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>年</InputLabel>
            <Select
              value={selectedYear}
              label="年"
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              {Array.from({ length: 5 }, (_, i) => dayjs().year() - 2 + i).map((year) => (
                <MenuItem key={year} value={year.toString()}>
                  {year}年
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>月</InputLabel>
            <Select
              value={selectedMonth}
              label="月"
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              {MONTHS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: colors.blueAccent[700] }}>
              <TableCell sx={{ fontWeight: "bold" }}>日付</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>状態</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>担当責任者</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>確認者</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>更新日時</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
              const record = records[day.toString()]
              const hasData = hasRecord(day)
              const localHoliday = isLocalHoliday(day)
              const masterHoliday = isMasterHoliday(day)
              const holiday = localHoliday || masterHoliday

              return (
                <TableRow key={day} hover>
                  <TableCell>
                    {selectedYear}年{getMonthLabel(selectedMonth)} {day}日
                  </TableCell>
                  <TableCell>
                    {holiday ? (
                      <Chip label="休業" color="default" size="small" />
                    ) : hasData ? (
                      <Chip label="入力済" color="success" size="small" />
                    ) : (
                      <Chip label="未入力" color="warning" size="small" />
                    )}
                  </TableCell>
                  <TableCell>{record?.manager || "-"}</TableCell>
                  <TableCell>{record?.verifier || "-"}</TableCell>
                  <TableCell>{record?.updatedAt || "-"}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      {localHoliday ? (
                        <Button
                          variant="outlined"
                          size="small"
                          color="secondary"
                          onClick={() => handleSetHoliday(day)}
                        >
                          休日解除
                        </Button>
                      ) : masterHoliday ? (
                        <Typography variant="body2" color="textSecondary">
                          休業日マスタで設定
                        </Typography>
                      ) : hasData ? (
                        <>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleEdit(day)}
                          >
                            編集
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            onClick={() => handleSetHoliday(day)}
                          >
                            休日設定
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="contained"
                            size="small"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={() => handleNew(day)}
                          >
                            入力
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleSetHoliday(day)}
                          >
                            休日設定
                          </Button>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default DailyHygieneManagement
